/**
 * POS Promo Code Entry Component
 * Allows cashiers to enter and validate coupon codes
 */
import { useState } from 'react';
import { 
  Tag, X, Loader2, Check, AlertCircle, Percent, DollarSign 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '../../utils/apiClient';

export default function POSPromoCode({ subtotal, customerId, onApplyDiscount, onClose }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);

  const validateCode = async () => {
    if (!code.trim()) {
      toast.error('Please enter a promo code');
      return;
    }
    
    setLoading(true);
    setValidationResult(null);
    
    try {
      const res = await api.post('/promo-codes/validate', {
        code: code.trim().toUpperCase(),
        order_subtotal: subtotal,
        customer_id: customerId
      });
      
      setValidationResult(res.data);
      
      if (res.data.valid) {
        toast.success(`Promo code valid! Discount: $${res.data.calculated_discount.toFixed(2)}`);
      }
    } catch (err) {
      toast.error('Failed to validate promo code');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (validationResult?.valid) {
      onApplyDiscount({
        code: validationResult.code,
        promoId: validationResult.promo_id,
        name: validationResult.name,
        discountType: validationResult.discount_type,
        discountValue: validationResult.discount_value,
        calculatedDiscount: validationResult.calculated_discount
      });
      onClose();
    }
  };

  // Common promo codes for quick access (could be fetched from API)
  const quickCodes = ['WELCOME10', 'STAFF50', 'VIP20', 'BIRTHDAY'];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="promo-code-modal">
      <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <Tag className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Apply Promo Code</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Enter customer coupon code</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-4 space-y-4">
          {/* Code Input */}
          <div className="flex gap-2">
            <Input
              placeholder="Enter promo code..."
              value={code}
              onChange={(e) => {
                setCode(e.target.value.toUpperCase());
                setValidationResult(null);
              }}
              onKeyDown={(e) => e.key === 'Enter' && validateCode()}
              className="flex-1 h-12 text-lg font-mono uppercase bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white"
              autoFocus
              data-testid="promo-code-input"
            />
            <Button
              onClick={validateCode}
              disabled={loading || !code.trim()}
              className="h-12 px-6 bg-green-600 hover:bg-green-700 text-white"
              data-testid="validate-promo-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Check'}
            </Button>
          </div>

          {/* Quick Codes */}
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Quick codes:</p>
            <div className="flex flex-wrap gap-2">
              {quickCodes.map((qc) => (
                <Button
                  key={qc}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setCode(qc);
                    setValidationResult(null);
                  }}
                  className="bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  {qc}
                </Button>
              ))}
            </div>
          </div>

          {/* Validation Result */}
          {validationResult && (
            <div className={`rounded-xl p-4 ${
              validationResult.valid 
                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
            }`}>
              {validationResult.valid ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                    <Check className="w-5 h-5" />
                    <span className="font-semibold">Valid Promo Code!</span>
                  </div>
                  
                  <div className="space-y-1">
                    <p className="font-medium text-green-800 dark:text-green-200">{validationResult.name}</p>
                    {validationResult.description && (
                      <p className="text-sm text-green-700 dark:text-green-300">{validationResult.description}</p>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between pt-3 border-t border-green-200 dark:border-green-700">
                    <div className="flex items-center gap-2">
                      {validationResult.discount_type === 'percent' ? (
                        <Percent className="w-5 h-5 text-green-600 dark:text-green-400" />
                      ) : (
                        <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" />
                      )}
                      <span className="text-sm text-green-700 dark:text-green-300">
                        {validationResult.discount_type === 'percent' 
                          ? `${validationResult.discount_value}% off`
                          : `$${validationResult.discount_value.toFixed(2)} off`
                        }
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-green-600 dark:text-green-400">Discount</p>
                      <p className="text-xl font-bold text-green-700 dark:text-green-300">
                        -${validationResult.calculated_discount.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
                  <AlertCircle className="w-5 h-5" />
                  <span>{validationResult.error || 'Invalid promo code'}</span>
                </div>
              )}
            </div>
          )}

          {/* Current Order Info */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500 dark:text-gray-400">Order Subtotal</span>
              <span className="font-medium text-gray-900 dark:text-white">${subtotal.toFixed(2)}</span>
            </div>
            {validationResult?.valid && (
              <div className="flex justify-between text-sm mt-1">
                <span className="text-green-600 dark:text-green-400">After Discount</span>
                <span className="font-bold text-green-600 dark:text-green-400">
                  ${(subtotal - validationResult.calculated_discount).toFixed(2)}
                </span>
              </div>
            )}
          </div>

          {/* Apply Button */}
          <Button
            onClick={handleApply}
            disabled={!validationResult?.valid}
            className="w-full h-14 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white font-semibold"
            data-testid="apply-promo-btn"
          >
            {validationResult?.valid ? (
              <>
                <Tag className="w-5 h-5 mr-2" />
                Apply Discount - Save ${validationResult.calculated_discount.toFixed(2)}
              </>
            ) : (
              'Enter a valid code to apply'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
